import { Button, Icon } from "@blueprintjs/core";
import { FieldGroup, FieldSet, showError, showSuccess, useApiEffect } from "nsitools-react";
import * as React from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";

import {
  BroodRequestApi,
  BroodRequestPotentialFathersApi,
  DogConditionDto,
  EDogSex,
  ERequestState,
  ErrorDetails,
} from "../../../../api";
import { FGSRSHCheckboxInput, SmallFormGenerator, ToolTipContainer } from "../../../../components";
import { useDialog, useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DogAs400ValidationFieldSet } from "./DogAs400ValidationFieldSet";
import { PotentialFathersValidation } from "./PotentialFathersValidation";
import { Popover2 } from "@blueprintjs/popover2";

export interface IDogValidationTabProps {
  broodRequestId: number;
  editMode: boolean;
}

export const DogValidationTab: React.FunctionComponent<IDogValidationTabProps> = ({ broodRequestId, editMode }) => {
  const api = useApiService(BroodRequestApi);
  const potentialFatherApi = useApiService(BroodRequestPotentialFathersApi);
  const { currentLanguage } = useGlobalData();
  const { pathname } = useLocation();
  const { showDialog } = useDialog();
  const { t, tUnsafe } = useTl();

  const [requestData, requestDataLoading] = useApiEffect(() => api.get(broodRequestId), []);

  const { data, loading, refresh } = useCrudApi<DogConditionDto[]>(
    React.useMemo(
      () => ({
        getApiFn: (id = broodRequestId) => api.getDogConditions(id, currentLanguage.languageId),
        saveApiFn: () => {},
        deleteApiFn: (d) => {},
        onDeletedRoute: () => "",
      }),
      [api, broodRequestId, currentLanguage.languageId, pathname]
    ),
    [broodRequestId]
  );

  React.useEffect(() => {
    if (currentLanguage) {
      refresh();
    }
  }, [currentLanguage, refresh]);

  const forceCondition = React.useCallback(
    async (dogNeededConditionId, dogId) => {
      try {
        showDialog({
          message: t(ETLCodes.ForceConditionConfirmationMessage),
          title: t(ETLCodes.ForceCoditionConfirmationTitle),
          onConfirmed: async () => {
            const res = await api.forceDogConditions(dogNeededConditionId, dogId, broodRequestId);
            showSuccess(t(ETLCodes.ConditionForcedsuccessfully));
            refresh();
            return res;
          },
        });
      } catch (err) {
        const details: ErrorDetails = JSON.parse(err.response);
        showError(tUnsafe(`BackendError_${details.errorCode}`));
      }
    },
    [api, broodRequestId, refresh, showDialog, t, tUnsafe]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const statusEditable = React.useMemo(
    () =>
      requestData?.request?.status === ERequestState.REQUESTED ||
      requestData?.request?.status === ERequestState.DOG_VALIDATION,
    [requestData]
  );

  const motherId = React.useMemo(() => data?.find((e) => e.concernedSex === EDogSex.Female).dogId, [data]);

  return (
    <>
      <PotentialFathersValidation
        broodRequestId={broodRequestId}
        editMode={editMode}
        editable={statusEditable}
        refresh={() => refresh()}
        requestData={requestData}
        addPotentialFatherLoading={loading}
      />

      <FieldSet title={t(ETLCodes.DogValidation)}>
        <SmallFormGenerator
          initialValues={{ conditions: data }}
          minLabelWidth={600}
          editMode={editMode}
          onSubmit={() => {}}
          loading={loading}
          hideButtons={true}
        >
          <FieldSet
            title={
              <>
                <Link target="_blank" to={`${ERoutes.dog}/${motherId}/detail/view`}>
                  {t(ETLCodes.MotherValidation)}
                </Link>
                {!data?.find((e) => e.concernedSex === EDogSex.Female)?.isGenealogyComplete && (
                  <Popover2
                    content={<ToolTipContainer>{t(ETLCodes.IncompleteGenealogy)}</ToolTipContainer>}
                    interactionKind={"hover"}
                  >
                    <div style={{ marginLeft: "0.5rem", marginRight: "0.5rem" }}>
                      <Icon icon={"warning-sign"} intent={"danger"} />
                    </div>
                  </Popover2>
                )}
              </>
            }
          >
            <FieldGroup>
              {data
                ?.find((e) => e.concernedSex === EDogSex.Female)
                ?.conditions?.map((d, index, key) => {
                  return (
                    <FieldGroup>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                        }}
                      >
                        <FGSRSHCheckboxInput
                          name={`conditions[${data.findIndex(
                            (i) => i.concernedSex === EDogSex.Female
                          )}].conditions[${index}].value`}
                          label={d.label}
                          readonly={true}
                        />
                        {editMode && !d.forced && !d.value && d.forceable && statusEditable ? (
                          <Button
                            style={{ maxHeight: "30px" }}
                            text={t(ETLCodes.Force)}
                            onClick={() =>
                              forceCondition(
                                d.dogNeededConditionId,
                                data?.find((e) => e.concernedSex === EDogSex.Female)?.dogId
                              )
                            }
                            disabled={d.value}
                          />
                        ) : d.forced ? (
                          <div style={{ color: "#B22222" }}>{t(ETLCodes.Forced)}</div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </FieldGroup>
                  );
                })}
            </FieldGroup>
          </FieldSet>
          <FieldSet title={t(ETLCodes.PotentialFathersValidation)}>
            <FieldGroup>
              {data
                ?.filter((e) => e.concernedSex === EDogSex.Male)
                .map((e, eIndex) => {
                  return (
                    <FieldSet
                      title={
                        <>
                          <Link target="_blank" to={`${ERoutes.dog}/${e.dogId}/detail/view`}>
                            {e.name}
                          </Link>
                          {!e?.isGenealogyComplete && (
                            <Popover2
                              content={<ToolTipContainer>{t(ETLCodes.IncompleteGenealogy)}</ToolTipContainer>}
                              interactionKind={"hover"}
                            >
                              <div style={{ marginLeft: "0.5rem", marginRight: "0.5rem" }}>
                                <Icon icon={"warning-sign"} intent={"danger"} />
                              </div>
                            </Popover2>
                          )}
                        </>
                      }
                    >
                      {e.conditions.map((d, index, key) => {
                        return (
                          <FieldGroup>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-start",
                              }}
                            >
                              <FGSRSHCheckboxInput
                                name={`conditions[${data.findIndex((i) => i === e)}].conditions[${index}].value`}
                                label={d.label}
                                readonly={true}
                              />
                              {editMode && !d.forced && !d.value && d.forceable && statusEditable ? (
                                <Button
                                  style={{ maxHeight: "30px" }}
                                  text={t(ETLCodes.Force)}
                                  onClick={() => forceCondition(d.dogNeededConditionId, e.dogId)}
                                  disabled={d.value}
                                />
                              ) : d.forced ? (
                                <div style={{ color: "#B22222" }}>{t(ETLCodes.Forced)}</div>
                              ) : (
                                <></>
                              )}
                            </div>
                          </FieldGroup>
                        );
                      })}
                    </FieldSet>
                  );
                })}
            </FieldGroup>
          </FieldSet>
        </SmallFormGenerator>
      </FieldSet>
      <DogAs400ValidationFieldSet broodRequestId={broodRequestId} />
    </>
  );
};
